import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ServiceMarketplaceDto } from "@green0meter/api-client";

import ServiceCard from "@components/main/serviceMarketplace/ServiceCard";
import { useGetServices } from "@api";
import { Card, CardContent, Stack, Typography, Button, Box } from "@components/base";
import { useUserStore } from "@store/user";

interface Component {}

const Component: FC<Component> = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { services, servicesEnabled, isPendingServices } = useGetServices(user);
  const [filteredServices, setFilteredServices] = useState<ServiceMarketplaceDto[]>(
    [],
  );
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);

  const getUniqueServiceTypes = (services: ServiceMarketplaceDto[]): string[] => {
    const types = services.map((service) => service.sertice_type).filter(Boolean);

    return [...new Set(types)];
  };

  useEffect(() => {
    if (!isPendingServices && services) {
      let filtered = services;

      // Filter by service types if any are selected
      if (selectedServiceTypes.length > 0) {
        filtered = filtered.filter((service) =>
          selectedServiceTypes.includes(service.sertice_type),
        );
      }

      setFilteredServices(filtered);
    }
  }, [services, isPendingServices, selectedServiceTypes]);

  const handleServiceTypeToggle = (serviceType: string): void => {
    setSelectedServiceTypes((prev) =>
      prev.includes(serviceType)
        ? prev.filter((type) => type !== serviceType)
        : [...prev, serviceType],
    );
  };

  if (isPendingServices) {
    return <div>{"Loading..."}</div>;
  }

  return (
    servicesEnabled && (
      <Card id="services-marketplace-list">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={3}
          pb={0}
        >
          <Stack spacing={1}>
            <Typography variant="h4">{t("servicesMarketplace.title")}</Typography>
          </Stack>
        </Stack>
        <CardContent>
          {services && services.length > 0 ? (
            <Stack spacing={2}>
              {/* Service Type Filter */}
              <Box>
                <Typography variant="subtitle2" mb={1}>
                  {t("servicesMarketplace.filterByType")}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {getUniqueServiceTypes(services).map((serviceType) => (
                    <Button
                      key={serviceType}
                      variant={
                        selectedServiceTypes.includes(serviceType)
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => handleServiceTypeToggle(serviceType)}
                      size="small"
                    >
                      {t(`serviceMarketplace.${serviceType}`)}
                    </Button>
                  ))}
                </Stack>
              </Box>
              <Typography>
                {"Found "}
                {filteredServices.length}
                {" services"}
              </Typography>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "16px",
                }}
              >
                {filteredServices.map((service) => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            </Stack>
          ) : (
            <Typography>{t("servicesMarketplace.emptyMessage")}</Typography>
          )}
        </CardContent>
      </Card>
    )
  );
};

export default Component;
